import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import { CustomButton, CustomLoadingButton } from "src/design";
import { CreateFeatureSwitchRequest } from "src/data/admin/types/featureSwitch";
import { colorActionPrimary, colorActionPrimaryHover } from "src/design/tokens";

interface CreateFeatureSwitchDialogProps {
  open: boolean;
  loading: boolean;
  /** Existing switch names, used to reject a duplicate before it reaches the API. */
  existingNames: string[];
  onClose: () => void;
  onCreate: (payload: CreateFeatureSwitchRequest) => void;
}

export const CreateFeatureSwitchDialog = ({
  open,
  loading,
  existingNames,
  onClose,
  onCreate,
}: CreateFeatureSwitchDialogProps) => {
  const [name, setName] = useState("");
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setActive(false);
    }
  }, [open]);

  const trimmedName = name.trim();
  const duplicate = existingNames.some(
    (existing) => existing.toLowerCase() === trimmedName.toLowerCase(),
  );

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleCreate = () => {
    if (!trimmedName || duplicate) return;
    onCreate({ name: trimmedName, active });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>New Feature Switch</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            error={duplicate}
            helperText={
              duplicate
                ? "A feature switch with this name already exists."
                : "Must be unique (e.g. use_ramadan_style, enable_ramadan_tag)"
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colorActionPrimary,
                    "&:hover": { backgroundColor: colorActionPrimaryHover },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: colorActionPrimary,
                  },
                }}
              />
            }
            label="Active on creation"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton color="inverted" onClick={handleClose} disabled={loading}>
          Cancel
        </CustomButton>
        <CustomLoadingButton
          loading={loading}
          disabled={!trimmedName || duplicate}
          onSubmit={handleCreate}
          label="Create"
          color="success"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
